import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, getMe, type Me } from '@/lib/api';

/**
 * The current session.
 *
 * A hook rather than a fetch inside `App` because every surface will need the
 * caller's role and grants, and the alternative is prop-drilling plus a second
 * copy of this state machine.
 *
 * **This is not authorization.** SEC-5 puts that in the API, enforced per
 * request from the token-resolved identity. What comes back here decides what
 * the UI shows; it never decides what the user may do.
 */

export type MeState =
  | { kind: 'loading' }
  | { kind: 'loaded'; me: Me }
  /**
   * `status` is carried because 403 and 500 call for different copy: a refusal
   * is permanent and retrying it is cruel, a server error is worth another go.
   * `0` is a network-level failure — see `ApiError` in `lib/api`.
   */
  | { kind: 'error'; status: number };

export interface UseMeResult {
  state: MeState;
  retry: () => void;
}

/**
 * **Exactly one call site: `App.tsx`.**
 *
 * `initialMe` is the sign-in gate's prefetch, handed down through `Bootstrap`
 * (ADR-0034). It seeds the state and skips the first fetch, so a ceremony that
 * has already spent three seconds hands over to a loaded app rather than to a
 * second loading line. An absent seed — the prefetch failed, or nothing
 * prefetched — behaves exactly as this hook always did.
 *
 * A second would issue a second `GET /me` and create a second state machine
 * that can disagree with the first — one loaded, one erroring, two answers to
 * "am I an admin". Surfaces take what they need as props until something needs
 * more than `role`, at which point this becomes a context in that slice.
 */
export function useMe(initialMe?: Me): UseMeResult {
  const [state, setState] = useState<MeState>(
    initialMe === undefined ? { kind: 'loading' } : { kind: 'loaded', me: initialMe },
  );
  const [reloadToken, setReloadToken] = useState(0);
  /*
    **The seed skips the first fetch only.** `retry()` still refetches, and it
    has to: `MockPersonaSwitch`, `MockPlanSwitch`, and `SessionGate`'s own retry
    button all go through it, and a seed that disabled fetching outright would
    freeze all three at whatever the gate saw.

    A ref rather than a `reloadToken === 0` test, because that reads as "the
    first render" when what it means is "we have not fetched yet" — and the two
    stop agreeing the moment anything else touches the token.
  */
  const seeded = useRef(initialMe !== undefined);

  useEffect(() => {
    if (seeded.current) {
      seeded.current = false;
      return;
    }

    const controller = new AbortController();

    getMe(controller.signal)
      .then((me) => {
        setState({ kind: 'loaded', me });
      })
      .catch((error: unknown) => {
        // An abort is our own doing, not a failure. Reporting it would flash an
        // error on a component that is already gone.
        if (error instanceof Error && error.name === 'AbortError') return;
        setState({ kind: 'error', status: error instanceof ApiError ? error.status : 0 });
      });

    return () => {
      controller.abort();
    };
  }, [reloadToken]);

  // The loading reset belongs in the handler, not the effect: calling setState
  // in an effect body triggers a cascading render.
  const retry = useCallback(() => {
    setState({ kind: 'loading' });
    setReloadToken((current) => current + 1);
  }, []);

  return { state, retry };
}
